
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { TreePine, ChevronRight, ChevronDown, Unlink, Search, User, Hash } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

interface HierarchyRecord {
  id: string;
  creator_name: string;
  social_media_handler: string;
  social_media_type: string;
  parent_id: string | null;
  children?: HierarchyRecord[];
}

interface HierarchyViewerProps {
  hierarchyData: HierarchyRecord[];
  onUnlinkRecord: (childId: string) => void;
  onLinkRecords: (parentId: string, childId: string) => void;
  isPending: (id: string) => boolean;
}

export const HierarchyViewer: React.FC<HierarchyViewerProps> = ({
  hierarchyData,
  onUnlinkRecord,
  onLinkRecords,
  isPending
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  const toggleNode = (nodeId: string) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const filterHierarchy = (records: HierarchyRecord[], searchTerm: string): HierarchyRecord[] => {
    if (!searchTerm.trim()) return records;
    
    const searchLower = searchTerm.toLowerCase();
    
    return records.filter(record => {
      const matches = record.creator_name.toLowerCase().includes(searchLower) ||
                     record.social_media_handler.toLowerCase().includes(searchLower) ||
                     record.social_media_type.toLowerCase().includes(searchLower);
      
      const hasMatchingChild = record.children?.some(child => 
        child.creator_name.toLowerCase().includes(searchLower) ||
        child.social_media_handler.toLowerCase().includes(searchLower) ||
        child.social_media_type.toLowerCase().includes(searchLower)
      );
      
      return matches || hasMatchingChild;
    });
  };

  const HierarchyNode: React.FC<{ 
    record: HierarchyRecord; 
    level: number; 
    isChild?: boolean;
  }> = ({ record, level, isChild = false }) => {
    const hasChildren = record.children && record.children.length > 0;
    const isExpanded = expandedNodes.has(record.id);
    const pending = isPending(record.id);

    return (
      <div className="space-y-2">
        <Card className={`${pending ? 'ring-2 ring-blue-400 bg-blue-50 dark:bg-blue-900/20' : ''}`}>
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <div style={{ marginLeft: `${level * 20}px` }} className="flex items-center gap-2">
                {hasChildren ? (
                  <Collapsible open={isExpanded} onOpenChange={() => toggleNode(record.id)}>
                    <CollapsibleTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </Button>
                    </CollapsibleTrigger>
                  </Collapsible>
                ) : (
                  <div className="w-6" />
                )}
                
                <div className="flex items-center gap-2">
                  {isChild ? (
                    <Badge variant="secondary" className="text-xs">Child</Badge>
                  ) : hasChildren ? (
                    <Badge variant="default" className="text-xs">Parent</Badge>
                  ) : (
                    <Badge variant="outline" className="text-xs">Standalone</Badge>
                  )}
                  
                  <User className="h-4 w-4 text-gray-500" />
                </div>
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate">{record.creator_name}</div>
                <div className="text-xs text-gray-500 flex items-center gap-2">
                  <Hash className="h-3 w-3" />
                  <span className="truncate">{record.social_media_handler || 'No handler'}</span>
                  <Badge variant="outline" className="text-xs">
                    {record.social_media_type || 'Unknown'}
                  </Badge>
                </div>
              </div>
              
              {isChild && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onUnlinkRecord(record.id)}
                  disabled={pending}
                  className="text-xs"
                >
                  <Unlink className="h-3 w-3 mr-1" />
                  Unlink
                </Button>
              )}
              
              {pending && (
                <Badge variant="secondary" className="text-xs">
                  Processing...
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>
        
        {hasChildren && (
          <Collapsible open={isExpanded}>
            <CollapsibleContent className="space-y-2">
              {record.children?.map(child => (
                <HierarchyNode
                  key={child.id}
                  record={child}
                  level={level + 1}
                  isChild={true}
                />
              ))}
            </CollapsibleContent>
          </Collapsible>
        )}
      </div>
    );
  };

  const filteredData = filterHierarchy(hierarchyData, searchTerm);
  const totalParents = hierarchyData.filter(record => record.children && record.children.length > 0).length;
  const totalChildren = hierarchyData.reduce((acc, record) => acc + (record.children?.length || 0), 0);

  return (
    <div className="space-y-6">
      {/* Search and Summary */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search hierarchy..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8"
          />
        </div>
        
        <div className="flex gap-4 text-sm text-muted-foreground">
          <span className="flex items-center gap-1">
            <TreePine className="h-4 w-4" />
            {totalParents} Parents
          </span>
          <span>{totalChildren} Children</span>
        </div>
      </div>

      {/* Hierarchy Display */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TreePine className="h-5 w-5" />
            Parent-Child Hierarchy
            <Badge variant="secondary">{filteredData.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {filteredData.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {searchTerm ? `No records found matching "${searchTerm}"` : 'No hierarchy relationships found'}
            </div>
          ) : (
            filteredData.map(record => (
              <HierarchyNode
                key={record.id}
                record={record}
                level={0}
              />
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
};
